import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  PlusIcon,
  XIcon,
  LockIcon,
  ArrowLeftIcon,
  StackIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';

import useCreateCoreEnum from '@/api/core/core-enums/useCreateCoreEnum';
import useUpdateCoreEnum from '@/api/core/core-enums/useUpdateCoreEnum';
import { CoreEnumDTO } from '@/api/core/core-enums/useCoreEnums';
import useCoreEnums from '@/api/core/core-enums/useCoreEnums';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { AuthContext } from '@/context/auth.context';
import { Switch } from '@/components/ui/switch';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin as checkIsAdmin } from '@/utils/user';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface EnumOption {
  id?: string;
  name: string;
  description: string;
  sortOrder: number;
}

interface EnumDetailPageProps {
  enumToEdit?: CoreEnumDTO | null;
  isNew?: boolean;
  onClose?: () => void;
}

export function EnumDetailPage({
  enumToEdit,

  isNew = false,
  onClose,
}: EnumDetailPageProps) {
  const { data: allEnums } = useCoreEnums({ includeOptions: true });
  const enumData = allEnums?.edges.find((e) => e.id === enumToEdit?.id);

  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const isAdmin = checkIsAdmin(currentUser, viewMode);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSystem, setIsSystem] = useState(false);
  const [areOptionsLocked, setAreOptionsLocked] = useState(false);
  const [options, setOptions] = useState<EnumOption[]>([]);

  const [baseline, setBaseline] = useState<{
    name: string;
    description: string;
    isSystem: boolean;
    areOptionsLocked: boolean;
    options: EnumOption[];
  } | null>(null);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const isEditMode = !!enumData || !!enumToEdit;
  const actualEnum = enumData || enumToEdit;

  const { mutate: createEnum, isPending: isCreating } = useCreateCoreEnum(
    () => {
      onClose?.();
    },
  );
  const { mutate: updateEnum, isPending: isUpdating } = useUpdateCoreEnum(
    enumData?.id || enumToEdit?.id || '',
    () => {
      // Update baseline after successful update so unsaved changes banner clears
      setBaseline({
        name,
        description,
        isSystem,
        areOptionsLocked,
        options,
      });
    },
  );

  const isSubmitting = isCreating || isUpdating;

  // Initialize form when enum data loads
  useEffect(() => {
    if (actualEnum) {
      const initialOptions =
        actualEnum.options?.map((opt) => ({
          id: opt.id,
          name: opt.name || '',
          description: opt.description || '',
          sortOrder: opt.sortOrder,
        })) || [];

      setName(actualEnum.name);
      setDescription(actualEnum.description);
      setIsSystem(actualEnum.isSystem || false);
      setAreOptionsLocked(actualEnum.areOptionsLocked);
      setOptions(initialOptions);
      setBaseline({
        name: actualEnum.name,
        description: actualEnum.description,
        isSystem: actualEnum.isSystem || false,
        areOptionsLocked: actualEnum.areOptionsLocked,
        options: initialOptions,
      });
    }
  }, [actualEnum]);

  const handleAddOption = () => {
    setOptions([
      ...options,
      {
        name: '',
        description: '',
        sortOrder: options.length,
      },
    ]);
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleOptionChange = (
    index: number,
    field: keyof EnumOption,
    value: string | number,
  ) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setOptions(newOptions);
  };

  const hasChanges = useMemo(() => {
    if (!isEditMode || !baseline) return false;

    // Check if name, description, isSystem, or areOptionsLocked changed
    if (
      name !== baseline.name ||
      description !== baseline.description ||
      isSystem !== baseline.isSystem ||
      areOptionsLocked !== baseline.areOptionsLocked
    ) {
      return true;
    }

    // Check if options length changed
    if (options.length !== baseline.options.length) {
      return true;
    }

    // Check if any option values changed
    for (let i = 0; i < options.length; i++) {
      const currentOpt = options[i];
      const baselineOpt = baseline.options[i];
      if (
        !baselineOpt ||
        currentOpt.name !== baselineOpt.name ||
        currentOpt.description !== baselineOpt.description
      ) {
        return true;
      }
    }

    return false;
  }, [
    name,
    description,
    isSystem,
    areOptionsLocked,
    options,
    isEditMode,
    baseline,
  ]);

  const handleDiscardChanges = () => {
    if (actualEnum) {
      setName(actualEnum.name);
      setDescription(actualEnum.description);
      setIsSystem(actualEnum.isSystem || false);
      setAreOptionsLocked(actualEnum.areOptionsLocked);
      setOptions(
        actualEnum.options?.map((opt) => ({
          id: opt.id,
          name: opt.name || '',
          description: opt.description || '',
          sortOrder: opt.sortOrder,
        })) || [],
      );
    }
  };

  const handleSubmit = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmUpdate = () => {
    const optionsPayload = options.map((opt, index) => ({
      id: opt.id,
      name: opt.name,
      description: opt.description,
      sortOrder: index,
    }));

    if (isEditMode && actualEnum) {
      updateEnum({
        name,
        description,
        ...(isAdmin && { isSystem }), // Only admins can toggle isSystem
        ...(isAdmin && { areOptionsLocked }), // Only admins can toggle areOptionsLocked
        options: optionsPayload,
      });
    } else {
      createEnum({
        name,
        description,
        ...(isAdmin && { isSystem }), // Only admins can create system enums
        // areOptionsLocked is always false on creation (backend enforces this)
        options: optionsPayload,
      });
    }
    setShowConfirmDialog(false);
  };

  const canSubmit = useMemo(() => {
    if (isEditMode) {
      return hasChanges && !isSubmitting;
    }
    return name.trim() && description.trim() && !isSubmitting;
  }, [name, description, isEditMode, hasChanges, isSubmitting]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6 md:p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center gap-3">
          <button
            onClick={() => onClose?.()}
            className="p-2 hover:bg-white rounded-lg transition-all duration-200 text-slate-600 hover:text-slate-900 hover:shadow-sm"
            title="Go back"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              {isEditMode ? 'Edit Enum' : 'Create New Enum'}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {isEditMode && actualEnum?.isSystem
                ? 'Manage this system enum'
                : 'Define a new enumeration with custom options'}
            </p>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {/* System Enum Alert */}
          {isEditMode && actualEnum?.isSystem && (
            <div className="px-6 py-4 bg-blue-50 border-b border-blue-200">
              <div className="flex gap-3">
                <LockIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-blue-900 text-sm">
                    System Enum
                  </p>
                  <p className="text-xs text-blue-800 mt-0.5">
                    This enum is managed by the system. You can only modify the
                    options below. Name and description are locked.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Locked Options Alert */}
          {isEditMode && areOptionsLocked && !isAdmin && (
            <div className="px-6 py-4 bg-purple-50 border-b border-purple-200">
              <div className="flex gap-3">
                <LockIcon className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-purple-900 text-sm">
                    Locked Enum Options
                  </p>
                  <p className="text-xs text-purple-800 mt-0.5">
                    The options for this enum are locked and cannot be modified.
                    Please contact an administrator for changes.
                  </p>
                </div>
              </div>
            </div>
          )}

          {isEditMode && hasChanges && (
            <div className="px-6 py-4 bg-amber-50 border-b border-amber-200">
              <div className="flex gap-3">
                <WarningCircleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-900 text-sm">
                    Unsaved changes
                  </p>
                  <p className="text-xs text-amber-800 mt-0.5">
                    You have unsaved changes. Discard or save them.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="p-6 md:p-8 space-y-8">
            {/* Name Field */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="name"
                  className="text-sm font-semibold text-slate-900"
                >
                  Name
                </Label>
                {isEditMode && actualEnum?.isSystem && (
                  <LockIcon className="h-4 w-4 text-slate-400" />
                )}
              </div>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Rebate Tags"
                disabled={isEditMode && actualEnum?.isSystem}
                className={`h-10 text-sm ${
                  isEditMode && actualEnum?.isSystem
                    ? 'bg-slate-50 text-slate-500'
                    : ''
                }`}
              />
              <p className="text-xs text-slate-500">
                {name.length} character{name.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Description Field */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="description"
                  className="text-sm font-semibold text-slate-900"
                >
                  Description
                </Label>
                {isEditMode && actualEnum?.isSystem && (
                  <LockIcon className="h-4 w-4 text-slate-400" />
                )}
              </div>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this enum is used for..."
                rows={3}
                required={true}
                disabled={isEditMode && actualEnum?.isSystem}
                className={`text-sm resize-none ${
                  isEditMode && actualEnum?.isSystem
                    ? 'bg-slate-50 text-slate-500'
                    : ''
                }`}
              />
              <p className="text-xs text-slate-500">
                {description.length} character
                {description.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Admin-Only Settings */}
            {isAdmin && (
              <div className="space-y-4 border-t border-slate-200 pt-6">
                {/* System Enum Toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label
                      htmlFor="isSystem"
                      className="text-sm font-semibold text-slate-900"
                    >
                      System Enum
                    </Label>
                    <p className="text-xs text-slate-500">
                      System enums have protected names and descriptions (only
                      options can be modified)
                    </p>
                  </div>
                  <Switch
                    id="isSystem"
                    checked={isSystem}
                    onCheckedChange={setIsSystem}
                  />
                </div>

                {/* Options Lock Toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label
                      htmlFor="areOptionsLocked"
                      className="text-sm font-semibold text-slate-900"
                    >
                      Lock Options
                    </Label>
                    <p className="text-xs text-slate-500">
                      Prevent non-admin users from creating, editing, or
                      deleting enum options
                    </p>
                  </div>
                  <Switch
                    id="areOptionsLocked"
                    checked={areOptionsLocked}
                    onCheckedChange={setAreOptionsLocked}
                  />
                </div>
              </div>
            )}

            {/* Options Section */}
            <div className="space-y-4 border-t border-slate-200 pt-8 pb-8">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg">
                    <StackIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      Enum Values
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                      {options.length} option{options.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>

              {options.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center">
                  <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                    <StackIcon className="h-6 w-6 text-slate-400" />
                  </div>
                  <p className="text-sm font-medium text-slate-600">
                    No options yet
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    Add values that can be selected when using this enum
                  </p>
                </div>
              ) : (
                <div className="space-y-3 mt-4">
                  {options.map((option, index) => (
                    <div
                      key={index}
                      className="group p-4 border border-slate-200 rounded-lg
                        bg-gradient-to-br from-white to-slate-50 hover:border-slate-300
                        hover:shadow-sm transition-all duration-200"
                    >
                      <div className="flex gap-3">
                        <div className="flex-1 space-y-2">
                          <Input
                            value={option.name}
                            onChange={(e) =>
                              handleOptionChange(index, 'name', e.target.value)
                            }
                            placeholder="Option name"
                            disabled={areOptionsLocked && !isAdmin}
                            className={`text-sm font-medium h-9 bg-white
                              placeholder-slate-400 ${
                                areOptionsLocked && !isAdmin
                                  ? 'bg-slate-50 text-slate-500'
                                  : ''
                              }`}
                          />
                          <Input
                            value={option.description}
                            onChange={(e) =>
                              handleOptionChange(
                                index,
                                'description',
                                e.target.value,
                              )
                            }
                            placeholder="Option description (optional)"
                            disabled={areOptionsLocked && !isAdmin}
                            className={`text-xs h-9 bg-white text-slate-600
                              placeholder-slate-400 ${
                                areOptionsLocked && !isAdmin
                                  ? 'bg-slate-50 text-slate-500'
                                  : ''
                              }`}
                          />
                        </div>
                        {!(areOptionsLocked && !isAdmin) && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveOption(index)}
                            className="opacity-0 group-hover:opacity-100 hover:bg-red-100
                              hover:text-red-600 transition-all duration-200 flex-shrink-0"
                          >
                            <XIcon className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!(areOptionsLocked && !isAdmin) && (
                <Button
                  type="button"
                  onClick={handleAddOption}
                  variant="outline"
                  className="w-full gap-2 border-dashed border-slate-300 text-slate-600
                    hover:bg-slate-50 hover:border-slate-400 transition-all duration-200"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span className="text-sm">Add Value</span>
                </Button>
              )}
            </div>

            {/* Action Buttons Footer */}
            <div className="flex gap-3 justify-end pt-6">
              <Button
                variant="outline"
                onClick={handleDiscardChanges}
                disabled={isSubmitting}
                className="text-slate-600"
              >
                Discard Changes
              </Button>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className="gap-2 bg-gradient-to-r from-emerald-600
                  to-emerald-700 hover:from-emerald-700 hover:to-emerald-800
                  transition-all duration-200 disabled:opacity-50"
                      >
                        {isSubmitting
                          ? 'Saving...'
                          : isEditMode
                            ? 'Update Enum'
                            : 'Create Enum'}
                      </Button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    Fill out name and description to create a new enum.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmUpdate}
        title={isEditMode ? 'Update Enum' : 'Create Enum'}
        message={
          isEditMode
            ? 'Are you sure you want to update this enum? This action may affect existing data.'
            : 'Create a new enum with the values you have defined.'
        }
        confirmText={isEditMode ? 'Update' : 'Create'}
        cancelText="Cancel"
        loading={isSubmitting}
      />
    </div>
  );
}
